import {
  Accordion,
  Card,
  CardContent,
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  ScrollArea,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import type {
  ICompanyApplicationTenant,
  IHomeLaunchpadRecentlyUsedSolution,
} from "@models";
import type { IAppLaunchRequest } from "@query";
import { AppWindow, FilterX, type LucideIcon, SearchX } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import {
  HOME_LAUNCHPAD_EMPTY_STATES,
  type HomeLaunchpadEmptyStateId,
} from "../homeLaunchpadEmpty.constants";
import { getHomeApplicationFilterValue } from "../homeLaunchpadFilters.utils";
import { ApplicationSolutionsSection } from "./ApplicationSolutionsSection";
import {
  getHomeLaunchpadAppKey,
  HOME_LAUNCHPAD_SECTION_SKELETON_KEYS,
  HOME_SOLUTION_SKELETON_KEYS,
  HOME_SOLUTIONS_GRID_CLASS,
  RECENTLY_USED_SECTION_KEY,
} from "./homeSolutions.constants";
import { RecentlyUsedSolutionsSection } from "./RecentlyUsedSolutionsSection";

const HOME_LAUNCHPAD_EMPTY_STATE_ICONS: Record<
  HomeLaunchpadEmptyStateId,
  LucideIcon
> = {
  noData: AppWindow,
  noCategoryResults: FilterX,
  noSearchResults: SearchX,
};

function renderLaunchpadEmptyState(
  stateId: HomeLaunchpadEmptyStateId,
  translate: (key: string, options?: { defaultValue?: string }) => string,
) {
  const message = HOME_LAUNCHPAD_EMPTY_STATES[stateId];
  const Icon = HOME_LAUNCHPAD_EMPTY_STATE_ICONS[stateId];

  return (
    <Empty className="min-h-0 max-w-3xl">
      <EmptyMedia variant="icon">
        <Icon />
      </EmptyMedia>
      <EmptyHeader>
        <EmptyTitle>
          {translate(message.labelKey, { defaultValue: message.defaultLabel })}
        </EmptyTitle>
      </EmptyHeader>
    </Empty>
  );
}

interface IHomeLaunchpadContentProps {
  tenants: ICompanyApplicationTenant[];
  recentlyUsedSolutions: IHomeLaunchpadRecentlyUsedSolution[];
  isLoading: boolean;
  companyUuid?: string;
  solutionsFetchEnabled: boolean;
  isLaunchPending: boolean;
  launchVariables?: IAppLaunchRequest;
  searchQuery?: string;
  selectedApplicationNames?: string[];
  onLaunchApp: (launchUrl: string, solutionId?: string) => void;
  userType: string | undefined;
  userName: string;
  userEmail: string;
  companyName: string;
  isInternalUser: boolean;
  isIRMDomain: boolean;
}

export function HomeLaunchpadContent({
  tenants,
  recentlyUsedSolutions,
  isLoading,
  companyUuid = "",
  solutionsFetchEnabled,
  isLaunchPending,
  launchVariables,
  searchQuery = "",
  selectedApplicationNames = [],
  onLaunchApp,
  userType,
  userName,
  userEmail,
  companyName,
  isInternalUser,
  isIRMDomain,
}: IHomeLaunchpadContentProps) {
  const { t } = useTranslation();
  const [solutionCountsByApp, setSolutionCountsByApp] = useState<
    Record<string, number>
  >({});

  const showRecentlyUsedSection = recentlyUsedSolutions.length > 0;

  const filteredTenants = useMemo(() => {
    if (selectedApplicationNames.length === 0) {
      return tenants;
    }

    return tenants.filter((tenant) =>
      selectedApplicationNames.includes(getHomeApplicationFilterValue(tenant)),
    );
  }, [selectedApplicationNames, tenants]);

  const handleFilteredCountChange = useCallback(
    (appKey: string, count: number) => {
      setSolutionCountsByApp((previous) => {
        if (previous[appKey] === count) {
          return previous;
        }
        return {
          ...previous,
          [appKey]: count,
        };
      });
    },
    [],
  );

  // Reset reported counts when filter inputs change (avoid effect).
  const countsResetKey = `${searchQuery}\0${selectedApplicationNames.join("\0")}\0${filteredTenants
    .map(getHomeLaunchpadAppKey)
    .join("\0")}\0${showRecentlyUsedSection ? "1" : "0"}`;
  const [activeCountsResetKey, setActiveCountsResetKey] =
    useState(countsResetKey);
  if (countsResetKey !== activeCountsResetKey) {
    setActiveCountsResetKey(countsResetKey);
    setSolutionCountsByApp({});
  }

  const totalSolutionCount = useMemo(
    () =>
      Object.values(solutionCountsByApp).reduce((sum, count) => sum + count, 0),
    [solutionCountsByApp],
  );

  const defaultExpandedApps = useMemo(() => {
    const appKeys = filteredTenants.map(getHomeLaunchpadAppKey).filter(Boolean);

    if (showRecentlyUsedSection) {
      return [RECENTLY_USED_SECTION_KEY, ...appKeys];
    }

    if (!filteredTenants.length) return [];
    const firstKey = getHomeLaunchpadAppKey(filteredTenants[0]);
    return firstKey ? [firstKey] : [];
  }, [filteredTenants, showRecentlyUsedSection]);

  const expectedSectionCount =
    filteredTenants.length + (showRecentlyUsedSection ? 1 : 0);
  const reportedAppsCount = Object.keys(solutionCountsByApp).length;
  const allAppsReported =
    expectedSectionCount > 0 && reportedAppsCount === expectedSectionCount;
  const hasSearchResults =
    !searchQuery.trim() || !allAppsReported || totalSolutionCount > 0;

  const hasNoData =
    !isLoading && tenants.length === 0 && !showRecentlyUsedSection;
  const hasNoCategoryResults =
    !isLoading &&
    tenants.length > 0 &&
    filteredTenants.length === 0 &&
    !showRecentlyUsedSection;
  const hasNoSearchResults =
    !isLoading &&
    !!searchQuery.trim() &&
    allAppsReported &&
    totalSolutionCount === 0;

  const emptyStateId: HomeLaunchpadEmptyStateId | null = hasNoData
    ? "noData"
    : hasNoCategoryResults
      ? "noCategoryResults"
      : hasNoSearchResults
        ? "noSearchResults"
        : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
      <ScrollArea
        className="h-full w-full"
        viewportClassName="[&>div]:!block [&>div]:!w-full">
        <div className="mx-auto box-border flex w-full max-w-[90rem] flex-col gap-4 pb-8">
          {isLoading ? (
            <div className="flex flex-col gap-4">
              {HOME_LAUNCHPAD_SECTION_SKELETON_KEYS.map((skeletonKey) => (
                <Card key={skeletonKey} isLoading className="h-16 w-full">
                  <CardContent />
                </Card>
              ))}
              <div className={HOME_SOLUTIONS_GRID_CLASS}>
                {HOME_SOLUTION_SKELETON_KEYS.map((skeletonKey) => (
                  <Card
                    key={skeletonKey}
                    isLoading
                    className="h-[246px] w-full">
                    <CardContent />
                  </Card>
                ))}
              </div>
            </div>
          ) : emptyStateId ? (
            renderLaunchpadEmptyState(emptyStateId, t)
          ) : hasSearchResults ? (
            <Accordion
              embedded
              variant="ghost"
              iconPosition="left"
              collapsibleType="multiple"
              defaultValue={defaultExpandedApps}>
              {showRecentlyUsedSection ? (
                <RecentlyUsedSolutionsSection
                  recentlyUsedSolutions={recentlyUsedSolutions}
                  searchQuery={searchQuery}
                  isLaunchPending={isLaunchPending}
                  launchVariables={launchVariables}
                  onFilteredCountChange={handleFilteredCountChange}
                  onLaunchApp={onLaunchApp}
                />
              ) : null}
              {filteredTenants.map((appTenant) => {
                const appKey = getHomeLaunchpadAppKey(appTenant);

                return (
                  <ApplicationSolutionsSection
                    key={appKey}
                    appTenant={appTenant}
                    companyUuid={companyUuid}
                    solutionsFetchEnabled={solutionsFetchEnabled}
                    searchQuery={searchQuery}
                    isLaunchPending={isLaunchPending}
                    launchVariables={launchVariables}
                    onFilteredCountChange={handleFilteredCountChange}
                    onLaunchApp={onLaunchApp}
                    userType={userType}
                    userName={userName}
                    userEmail={userEmail}
                    companyName={companyName}
                    isInternalUser={isInternalUser}
                    isIRMDomain={isIRMDomain}
                  />
                );
              })}
            </Accordion>
          ) : null}
        </div>
      </ScrollArea>
    </div>
  );
}
